/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import lodash from "lodash";
import {
  EventActionResourceReportResult,
  ResourceReportPackage,
} from "~/generated/graphql";
import { useGetEventActionResourceReport } from "../../../hooks/useGetEventActionResourceReport/useGetEventActionResourceReport";
import { ResourceDetailsActionDetailsSidebarFilterType } from "../../../types/ResourceDetailsActionDetailsSidebarState";
import { useResourceDetailsActionDetailsState } from "./ResourceDetailsActionDetailsSidebarProvider";

type Props = {
  eventActionId: string;
  eventId: string;
  resourceId: string;
};

const filterPackagesByPhrase = (
  result: EventActionResourceReportResult | undefined,
  phrase: string | undefined
) => {
  const resourcesToReturn = lodash.cloneDeep(result);

  if (!phrase) {
    return resourcesToReturn;
  }

  const filterPackageByPhrase = (pkg: ResourceReportPackage) =>
    pkg.name.toLowerCase().includes(phrase.toLowerCase());

  return {
    ...result,
    installed: result?.installed?.filter(filterPackageByPhrase) ?? [],
    updated: result?.updated?.filter(filterPackageByPhrase) ?? [],
    available: result?.available?.filter(filterPackageByPhrase) ?? [],
    removed: result?.removed?.filter(filterPackageByPhrase) ?? [],
    failed: result?.failed?.filter(filterPackageByPhrase) ?? [],
  };
};

const filterPackagesByStatus = (
  result: EventActionResourceReportResult | undefined,
  status: ResourceDetailsActionDetailsSidebarFilterType
) => {
  const resourcesToReturn = lodash.cloneDeep(result);

  if (!status?.length) {
    return resourcesToReturn;
  }

  switch (status) {
    case ResourceDetailsActionDetailsSidebarFilterType.INSTALLED:
      return {
        ...result,
        installed: result?.installed ?? [],
        updated: [],
        available: [],
        removed: [],
        failed: [],
      };
    case ResourceDetailsActionDetailsSidebarFilterType.UPDATED:
      return {
        ...result,
        installed: [],
        updated: result?.updated ?? [],
        available: [],
        removed: [],
        failed: [],
      };
    case ResourceDetailsActionDetailsSidebarFilterType.AVAILABLE:
      return {
        ...result,
        installed: [],
        updated: [],
        available: result?.available ?? [],
        removed: [],
        failed: [],
      };
    case ResourceDetailsActionDetailsSidebarFilterType.REMOVED:
      return {
        ...result,
        installed: [],
        updated: [],
        available: [],
        removed: result?.removed ?? [],
        failed: [],
      };
    case ResourceDetailsActionDetailsSidebarFilterType.FAILED:
      return {
        ...result,
        installed: [],
        updated: [],
        available: [],
        removed: [],
        failed: result?.failed ?? [],
      };
    default:
      return resourcesToReturn;
  }
};

export function useGetFilteredActionDetails({
  eventActionId,
  eventId,
  resourceId,
}: Props) {
  const { data, loading, error } = useGetEventActionResourceReport({
    eventActionId,
    eventId,
    resourceId,
  });

  const packagesCounts = {
    [ResourceDetailsActionDetailsSidebarFilterType.ALL]:
      (data?.result?.installed?.length ?? 0) +
      (data?.result?.updated?.length ?? 0) +
      (data?.result?.available?.length ?? 0) +
      (data?.result?.removed?.length ?? 0) +
      (data?.result?.failed?.length ?? 0),
    [ResourceDetailsActionDetailsSidebarFilterType.INSTALLED]:
      data?.result?.installed?.length ?? 0,
    [ResourceDetailsActionDetailsSidebarFilterType.UPDATED]:
      data?.result?.updated?.length ?? 0,
    [ResourceDetailsActionDetailsSidebarFilterType.AVAILABLE]:
      data?.result?.available?.length ?? 0,
    [ResourceDetailsActionDetailsSidebarFilterType.REMOVED]:
      data?.result?.removed?.length ?? 0,
    [ResourceDetailsActionDetailsSidebarFilterType.FAILED]:
      data?.result?.failed?.length ?? 0,
  };

  const {
    actionDetailsState: { filterQuery, searchQuery },
  } = useResourceDetailsActionDetailsState();

  const filteredByPhrase = filterPackagesByPhrase(data?.result, searchQuery);

  const filteredActionDetails = filterPackagesByStatus(
    filteredByPhrase,
    filterQuery
  );

  return { filteredActionDetails, packagesCounts, loading, error };
}
